// @flow

import FroalaEditor from 'froala-editor';
import map from 'lodash/map';
import moment from 'moment-tz';

import {postJson} from 'app/utils/request';
import {
  generateS3Key,
  generateUUID,
  type ACLType,
} from 'app/utils/fineuploader';

type SettingsType = {
  uploadURL?: string,
  requestEndpoint: string,
  signatureEndpoint: string,
  accountId: number,
  regionName: string,
  accessKey: string,
  objACL: ACLType,
  bucket: string,
  mediaURL: string,
  objParams: {[string]: any},
};

FroalaEditor.PLUGINS.awsUploadSigner = function (editor) {
  const sweetConfig = editor.opts.sweetprocess;

  function getFileKey(file) {
    const original = file.name || `${generateUUID()}`;
    return generateS3Key(
      original,
      sweetConfig.mediaURL,
      'files',
      sweetConfig.accountId
    );
  }

  function formBuilderFactory(config: SettingsType, type: 'image' | 'video') {
    if (config.uploadURL == null) {
      config.uploadURL = config.requestEndpoint;
    }

    function buildAWSCredential(date: string): string {
      const {accessKey, regionName} = config;
      return `${accessKey}/${date}/${regionName}/s3/aws4_request`;
    }

    return function getS3SignedForm(file) {
      const now = moment().utc();
      const shortDate = now.format('YYYYMMDD');
      const longDate = `${shortDate}T${now.format('HHmmss')}Z`;

      // don't reuse the froalaFormData because the "file" field
      // must be the last one
      const formData = new FormData();

      const conditionsMap = {
        ...config.objParams,
        acl: config.objACL,
        bucket: config.bucket,
        'Content-Type': file.type,
        success_action_status: '201',
        key: getFileKey(file),
        'x-amz-algorithm': 'AWS4-HMAC-SHA256',
        'x-amz-date': longDate,
        'x-amz-credential': buildAWSCredential(shortDate),
      };

      const conditions = map(conditionsMap, (value, key: string) => {
        // populate the formData AND create the conditions array in
        // the same loop
        formData.append(key, value);
        return {[key]: value};
      });

      // don't send expiration key, so server will create a short live
      // token (~5m last time I checked)
      return postJson(config.signatureEndpoint + '?v4=true', {conditions}).then(
        (response) => {
          formData.append('policy', response.policy);
          formData.append('x-amz-signature', response.signature);

          // this always must be the last field appended to the form
          formData.append(editor.opts[`${type}UploadParam`], file);
          return formData;
        }
      );
    };
  }

  if (
    !editor.opts.getImageUploadForm &&
    editor.opts.imageUploadToS3 !== false
  ) {
    editor.opts.getImageUploadForm = formBuilderFactory(
      editor.opts.imageUploadToS3,
      'image'
    );
  }

  if (
    !editor.opts.getVideoUploadForm &&
    editor.opts.videoUploadToS3 !== false
  ) {
    editor.opts.getVideoUploadForm = formBuilderFactory(
      editor.opts.videoUploadToS3,
      'video'
    );
  }

  return {};
};
